import type { GetBrandDTO, MbcBrief } from "@nlc/shared-types";

import type { MBCReviewData } from "./types";

const EMPTY_BRAND_GUIDELINES: MBCReviewData["brandGuidelines"] = {
  colors: [],
  typography: [],
};

function humanize(value: string): string {
  return value
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(" ");
}

function capitalize(value: string): string {
  return value ? value[0]!.toUpperCase() + value.slice(1) : value;
}

function mapBrandGuidelines(
  brand: GetBrandDTO,
): MBCReviewData["brandGuidelines"] {
  return {
    colors: brand.colorPalette.map((color) => ({
      hex: color.hexCode,
      name: color.purpose ?? "",
      swatch: color.hexCode,
    })),
    typography: brand.typography.map((font) =>
      `${font.family} ${capitalize(font.variant)}`.trim(),
    ),
  };
}

function extractExtension(fileName: string): { ext?: string } {
  const match = fileName.match(/\.([\dA-Za-z]+)$/);

  if (!match) return {};
  const ext = match[1]?.toUpperCase();

  return ext ? { ext } : {};
}

function emptyReview(
  brandGuidelines: MBCReviewData["brandGuidelines"],
  variationNumber: number,
): MBCReviewData {
  return {
    additionalGuidelines: [],
    assets: { files: [], links: [] },
    brandGuidelines,
    brief: {
      description: "",
      fileSizes: [],
      fileTypes: [],
      textForDesign: "",
      title: "",
      typeTag: "",
      variationLabel: `Variation ${variationNumber}`,
    },
    briefSummary: [],
    pagination: { current: variationNumber, total: 1 },
    visualReferences: { images: [], links: [] },
  };
}

export function mapBriefToReview(
  brief: MbcBrief | null,
  variationNumber: number,
  brand?: GetBrandDTO | null,
): MBCReviewData {
  const brandGuidelines = brand
    ? mapBrandGuidelines(brand)
    : EMPTY_BRAND_GUIDELINES;

  if (!brief) return emptyReview(brandGuidelines, variationNumber);

  const variations = (brief.variations ?? []).filter(
    (variation): variation is NonNullable<typeof variation> =>
      variation != null,
  );
  const total = Math.max(1, variations.length);

  // Look up variation by variationNumber first. If no variations carry variationNumber
  // (e.g. legacy/unbackfilled brief), fall back to 1-based index.
  let foundIndex = variations.findIndex(
    (v) => v.variationNumber === variationNumber,
  );

  if (foundIndex === -1 && variations.every((v) => v.variationNumber == null)) {
    foundIndex =
      variationNumber >= 1 && variationNumber <= variations.length
        ? variationNumber - 1
        : -1;
  }

  const selected = foundIndex >= 0 ? variations[foundIndex] : null;
  // Display position in array order (1-based), never the raw variationNumber
  const currentPosition = foundIndex >= 0 ? foundIndex + 1 : 1;

  const fileTypes = (selected?.fileTypes ?? []).flatMap((type) =>
    type ? [type] : [],
  );

  const assetGroup = {
    files: [...(selected?.assets?.files ?? []), ...(brief.assets?.files ?? [])],
    links: [...(selected?.assets?.links ?? []), ...(brief.assets?.links ?? [])],
  };

  const designTypeLabel = brief.designType ? humanize(brief.designType) : "";

  return {
    // Stored as one free-text block; the card renders one line per row.
    additionalGuidelines: (brief.additionalGuidelines ?? "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean),
    assets: {
      files: assetGroup.files.map((file) => ({
        caption: file.name,
        src: file.url,
        ...extractExtension(file.name),
      })),
      links: assetGroup.links.map((link) => link.url),
    },
    brandGuidelines,
    brief: {
      description: selected?.description ?? "",
      fileSizes: (selected?.fileSizes ?? [])
        .filter((size): size is NonNullable<typeof size> => size != null)
        .map((size) => ({
          label: size.label ?? "",
          size: size.dimensions ?? "",
        })),
      fileTypes,
      textForDesign: selected?.textForDesign ?? "",
      title: brief.title ?? "",
      typeTag: designTypeLabel,
      variationLabel: `Variation ${currentPosition}`,
    },
    briefSummary: [
      {
        label: "Brand",
        value:
          brand?.name ??
          (brief.brandId == null ? "" : `Brand #${brief.brandId}`),
      },
      { label: "Type", value: designTypeLabel },
      { label: "Variations", value: String(total) },
    ],
    pagination: { current: currentPosition, total },
    visualReferences: {
      images: (brief.inspirations?.files ?? []).map((file) => ({
        caption: file.name,
        src: file.url,
        ...extractExtension(file.name),
      })),
      links: (brief.inspirations?.links ?? []).map((link) => link.url),
    },
  };
}
